import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  FormErrorMessage,
  ModalFooter,
  Input,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import Message from "@/components/common/AlertMessage";
import { useTranslation } from "react-i18next";
import axios from "axios";

type FormInputs = {
  name: string;
};

type Props = {
  id?: number;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  parentFolder: number | string;
};

const FolderModal = ({
  id,
  isOpen,
  onClose,
  onUpdate,
  parentFolder,
}: Props): React.ReactElement => {
  const {
    handleSubmit: folderFormHandleSubmit,
    register: folderFormRegister,
    reset: folderFormReset,
    formState: {
      errors: folderFormErrors,
      isSubmitting: folderFormIsSubmitting,
    },
  } = useForm<FormInputs>();
  const [message, setMessage] = useState(null);
  const { t } = useTranslation("common");

  const errorKeys = {
    DuplicateFolderName: t("errorKeys.duplicateFolderName"),
    NameLengthError: t("cloud.folderNameLengthError", {
      length: 35,
    }),
  };

  const onSubmit = (data: FormInputs) => {
    return axios
      .post(`/cloud/folder/${id || ""}`, {
        ...data,
        parentFolder,
      })
      .then(() => {
        onClose();
        onUpdate();
        setMessage({ success: true });
      })
      .catch((error) => setMessage({ error: errorKeys[error] }));
  };

  useEffect(() => {
    folderFormReset();
    setMessage(null);
  }, [isOpen]);

  return (
    <Modal
      motionPreset="slideInBottom"
      isOpen={isOpen}
      onClose={onClose}
      preserveScrollBarGap
      isCentered
    >
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>{t("cloud.addFolder")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {message?.error && (
              <Message status="error" message={message.error} />
            )}
            {message?.success && (
              <Message status="success" message={t("dataSaved")} />
            )}

            <FormControl isInvalid={!!folderFormErrors.name} isRequired>
              <FormLabel htmlFor="name">{t("name")}</FormLabel>
              <Input
                type="text"
                autoFocus
                {...folderFormRegister("name", {
                  required: true,
                  maxLength: 35,
                })}
              />
              {folderFormErrors.name && (
                <FormErrorMessage>
                  {folderFormErrors?.name?.type === "maxLength"
                    ? t("cloud.folderNameLengthError", {
                        length: 35,
                      })
                    : t("users.fieldRequired")}
                </FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button
              onClick={folderFormHandleSubmit((data) => onSubmit(data))}
              isLoading={folderFormIsSubmitting}
              ml={3}
            >
              {t("add")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default FolderModal;
